// src/pages/Register.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as z from 'zod';

import { AuthLayout } from '../components/AuthLayout';
import { Button } from 'components/Elements';
import { Form, InputField } from 'components/Form';
import { useAuth } from 'lib/auth';
import { useNavigate } from 'react-router-dom';

const schema = z
  .object({
    name: z.string().min(1, 'Full name is required'),
    email: z.string().email('Enter a valid email').min(1, 'Email is required'),
    phoneNumber: z.string().min(1, 'Phone number is required'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
    terms: z.boolean().refine((val) => val === true, 'You must agree to the terms'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

export function Register() {
  const [loading, setLoading] = useState(false);
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();

  return (
    <AuthLayout
      title="Create Account"
      subtitle="Join the Ayinza family and explore handcrafted excellence."
    >
      <Form
        onSubmit={async (values) => {
          setLoading(true);
          try {
            await registerUser({
              name: values.name,
              email: values.email,
              phoneNumber: values.phoneNumber,
              password: values.password,
            });
            navigate('/products');
          } catch (error) {
            setLoading(false);
          }
        }}
        schema={schema}
      >
        {({ register, formState }) => (
          <>
            <InputField
              label="Full Name"
              type="text"
              error={formState.errors['name']}
              registration={register('name')}
              placeholder="John Doe"
            />

            <InputField
              label="Email Address"
              type="email"
              error={formState.errors['email']}
              registration={register('email')}
              placeholder="you@example.com"
            />

            <InputField
              label="Phone Number"
              type="text"
              error={formState.errors['phoneNumber']}
              registration={register('phoneNumber')}
              placeholder="e.g +2567...."
            />

            <InputField
              label="Password"
              type="password"
              error={formState.errors['password']}
              registration={register('password')}
              placeholder="••••••••"
            />

            <InputField
              label="Confirm Password"
              type="password"
              error={formState.errors['confirmPassword']}
              registration={register('confirmPassword')}
              placeholder="••••••••"
            />

            <div className="flex items-center space-x-2">
              <input
                id="terms"
                type="checkbox"
                {...register('terms')}
                className="w-4 h-4 border-gray-300 text-[#AC3F1E] focus:ring-[#AC3F1E]"
              />
              <label htmlFor="terms" className="text-sm text-gray-600">
                I agree to the{' '}
                <a href="/terms" className="text-[#AC3F1E] hover:underline">
                  Terms & Conditions
                </a>
              </label>
            </div>
            {formState.errors['terms'] && (
              <p className="text-sm text-red-500 mt-1">{formState.errors['terms'].message}</p>
            )}

            <Button isLoading={loading} type="submit" className="w-full mt-4">
              Create Account
            </Button>
          </>
        )}
      </Form>
    </AuthLayout>
  );
}

Register.propTypes = {
  onSuccess: PropTypes.func,
};
